import type {TriggerState} from 'src/reducers/trigger';
import type {
  TriggerRule,
  TriggerApi,
  TriggerApiPayload,
} from 'src/types/trigger';

import uniqueId from 'lodash/uniqueId';
import {hashObject} from 'src/utils/index';


export const MAX_VALUES_TO_SHOW = 20;

export const getTriggerRule = (obj: Partial<TriggerRule>): TriggerRule => {
  const defaultTriggerRule = {
    field: null,
    fromValue: null,
    toValue: null,
    fromValueType: 'any',
    toValueType: 'specific',
    fieldDataType: null,
  };
  // @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string'.
  return {
    id: uniqueId('trigger_rule_'),
    ...defaultTriggerRule,
    ...obj,
  };
};

export const getTriggerRuleText = (rule: TriggerRule): string => {
  const fieldLabel = rule.field;
  return `${fieldLabel ?? ''} ${getTriggerRuleValueText(rule)}`;
};

export const getTriggerRuleValueText = (rule: TriggerRule): string => {
  const getValueText = (valueType: string, values?: Array<string> | null) => {
    if (valueType === 'any') {
      return 'any value';
    }
    if (values) {
      return values.length > 1 ? `any of '${values.length} values'` : values[0];
    }
    return '';
  };

  if (rule.fromValueType && rule.toValueType) {
    const fromVal = getValueText(rule.fromValueType, rule.fromValue);
    const toVal = getValueText(rule.toValueType, rule.toValue);
    return `changes from ${fromVal} to ${toVal}`;
  }

  return 'Changes from one value to another value, based on your selection';
};

export const parseTrigger = (
  trigger: TriggerApi,
): {
  rules: Array<TriggerRule>;
  postActivationOnly: boolean;
} => {
  const {next_nodes, body} = trigger;
  const postActivationOnly = body.body?.post_activation_only ?? false;
  const apiPayloadRules = next_nodes[0].condition.blocks[0].rules ?? [];
  const rules = apiPayloadRules
    .map((rule) => {
      const conditionsArray = rule.conditions ?? [];
      if (conditionsArray.length === 0) {
        return null;
      }
      const ruleCondition = conditionsArray[0] ?? {};

      const fromValue = ruleCondition?.value?.from ?? null;
      const toValue = ruleCondition?.value?.to ?? null;

      return {
        id: uniqueId('trigger_rule_'),
        field: ruleCondition.field ?? null,
        fromValue: fromValue ? parseTriggerValue(fromValue) : null,
        toValue: toValue ? parseTriggerValue(toValue) : null,
        fromValueType: fromValue !== null ? 'specific' : 'any',
        toValueType: toValue !== null ? 'specific' : 'any',
        fieldDataType: null,
      };
    })
    .filter(Boolean); // removes any nulls from the array
  // @ts-expect-error - TS2345 - Argument of type 'string | never[]' is not assignable to parameter of type 'TriggerRule[]'.
  return {rules, postActivationOnly};
};

export const normalizeTrigger = (trigger: TriggerState): TriggerApiPayload => {
  const {rules} = trigger;
  const apiPayloadRules = rules.reduce((acc, rule) => {
    if (rule.field != null) {
      const conditionsArray = [];
      conditionsArray[0] = {
        is_dynamic: true,
        field: rule.field,
        operator: 'changes',
        value: {
          from:
            rule.fromValueType === 'any'
              ? null
              : // @ts-expect-error - TS2345 - Argument of type 'string | never[]' is not assignable to parameter of type 'TriggerRule[]'.
                normalizeTriggerValue(rule.fromValue, rule.fieldDataType),
          to:
            rule.toValueType === 'any'
              ? null
              : // @ts-expect-error - TS2345 - Argument of type 'string | never[]' is not assignable to parameter of type 'TriggerRule[]'.
                normalizeTriggerValue(rule.toValue, rule.fieldDataType),
        },
      };
      // @ts-expect-error - TS2345 - Argument of type 'string | never[]' is not assignable to parameter of type 'TriggerRule[]'.
      acc.push({
        conditions: conditionsArray,
      });
    }
    return acc;
  }, []);

  return {
    conditions: {
      blocks: [
        {
          rules: apiPayloadRules,
        },
      ],
    },
  };
};

export const normalizeTriggerValue = (
  value: Array<string> | null,
  fieldDataType: string | null,
): Array<boolean | string | number> | null => {
  if (!value || value.length === 0) {
    return null;
  }

  if (value.length === 1 && (value[0] === 'true' || value[0] === 'false')) {
    return [value[0] === 'true'];
  }
  if (value.length === 1 && fieldDataType === 'number') {
    return [parseInt(value[0], 10)];
  }
  return [...value];
};

export const parseTriggerValue = (
  value?: Array<string | boolean | number> | null,
): Array<string> | null | undefined => {
  if (!value || value.length === 0) {
    return null;
  }
  if (
    (value.length === 1 && typeof value[0] === 'boolean') ||
    typeof value[0] === 'number'
  ) {
    return [String(value[0])];
  }
  const filteredValues = value.reduce<Array<any>>((acc, item) => {
    if (typeof item === 'string') {
      acc.push(item);
    }
    return acc;
  }, []);

  return filteredValues.length > 0 ? filteredValues : null;
};

export const getDuplicateRules = (rules: Array<TriggerRule>): Array<string> => {
  const ruleMap = rules.reduce((acc, rule) => {
    //ignore id field in hashing the object
    const {id, ...objToHash} = rule;
    const key = hashObject(objToHash) as string;
    if (!acc.has(key)) {
      return new Map([...acc, [key, [rule.id]]]);
    } else {
      const existingRules = acc.get(key);
      if (existingRules) {
        return new Map([...acc, [key, [...existingRules, rule.id]]]);
      }
    }
    return acc;
  }, new Map());
  return [...ruleMap.keys()].reduce<Array<any>>((acc, key) => {
    const rules = ruleMap.get(key) ?? [];
    if (rules.length > 1) {
      return [...acc, ...rules];
    }
    return acc;
  }, []);
};

export const getTriggerDescription = (
  rulesLength: number,
  postActivationOnly: boolean,
  t: (arg1: string, arg2: string) => string,
): string => {
  if (rulesLength === 0 && postActivationOnly === false) {
    return t(
      'For_existing_and_new_members_of_the_list_and_satisfies_following_conditions',
      'For existing and new members of the list and satisfies following conditions',
    );
  }
  if (rulesLength === 0 && postActivationOnly === true) {
    return t(
      'For_new_members_who_are_added_to_list_after_activation_and_satisfies_following_conditions',
      'For new members who are added to list after activation and satisfies following conditions',
    );
  }
  if (rulesLength > 0 && postActivationOnly === false) {
    return t(
      'For_existing_members_of_the_list_and_satisfies_following_conditions_before_activation',
      'For existing members of the list and satisfies following conditions before activation',
    );
  }
  if (rulesLength > 0 && postActivationOnly === true) {
    return t(
      'For_existing_and_new_members_of_the_list_and_satisfies_following_conditions_after_activation',
      'For existing and new members of the list and satisfies following conditions after activation',
    );
  }
  return '';
};
